import { Box, ListItem, paletteColors, useMediaQuery } from '@evaneos/b2b-ui';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { Notification, NotificationType } from '../../../../resources/notifications/types';
import { VisuallyHidden } from '../../../../shared/components/VisuallyHidden';
import { useUpdateNotificationRead } from '../../../../resources/notifications/hooks';
import { useTrackEventAmplitude } from '../../../../shared/analytics';
import { NewNotificationClickedProperties } from '../../../../shared/analytics/__AUTO_GENERATED__';
import { useAuth } from '../../../../shared/providers/AuthProvider';

import { ToggleReadButton } from './ToggleReadButton';
import { NotificationItemContent } from './NotificationItemContent';

export function NotificationItem({ notification }: Readonly<{ notification: Notification }>) {
    const { trackEvent } = useTrackEventAmplitude();
    const { formatMessage } = useIntl();
    const { mutate: updateNotificationReadStatus } = useUpdateNotificationRead();
    const supportsHover = useMediaQuery('(hover: hover)');
    const { isImpersonated } = useAuth();

    const handleToggleReadStatus = () => {
        trackEvent((ampli, defaultProperties) => {
            ampli.quickActionUsed({
                ...defaultProperties,
                action_type: notification.read ? 'notification_unread' : 'notification_read',
            });
        });

        updateNotificationReadStatus({
            notificationId: notification.id,
            action: notification.read ? 'unread' : 'read',
        });
    };

    const handleNotificationClick = () => {
        trackEvent((ampli, defaultProperties) => {
            const notificationTypeMap: Record<
                NotificationType,
                NewNotificationClickedProperties['notification_type']
            > = {
                'proposal.validated': 'proposal_validated',
                'instalment.paid': 'instalment_paid',
                'proposal.plan_accommodations_selected': 'accommodations_selected',
                'dossier.automatically_set_lost': 'automatically_set_lost',
            };
            ampli.newNotificationClicked({
                ...defaultProperties,
                request_id: notification.dossierId,
                notification_type: notificationTypeMap[notification.type],
                notification_read: notification.read,
            });
        });

        if (!notification.read && !isImpersonated) {
            updateNotificationReadStatus({
                notificationId: notification.id,
                action: 'read',
                options: {
                    showSuccessToast: false,
                },
            });
        }
    };

    return (
        <ListItem
            sx={{
                position: 'relative',
                '&:hover, &:has(:focus-visible)': {
                    background: {
                        xs: paletteColors.neutral[0],
                        md: paletteColors.neutral[100],
                        xxl: paletteColors.neutral[0],
                    },
                },
                '&:not(:last-child)': {
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                },
                marginBottom: 0,
                cursor: 'pointer',
                ...(supportsHover && {
                    '&:hover [data-toggle-read-status-btn], [data-toggle-read-status-btn]:focus-visible':
                        {
                            opacity: 1,
                        },
                }),
            }}
            disablePadding
        >
            <Box
                component={Link}
                to={`/dossier/${notification.dossierId}/discussion`}
                sx={{
                    width: '100%',
                    p: '12px 24px',
                    pr: '70px',
                    minHeight: '64px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                }}
                onClick={handleNotificationClick}
            >
                {!notification.read && (
                    <Box
                        component="span"
                        sx={{
                            width: 8,
                            height: 8,
                            flexShrink: 0,
                            borderRadius: '50%',
                            backgroundColor: 'info.500',
                            marginRight: 0.5,
                        }}
                    >
                        <VisuallyHidden>
                            {formatMessage({ id: 'homepage.notifications.unread' })}
                        </VisuallyHidden>
                    </Box>
                )}
                <NotificationItemContent notification={notification} />
            </Box>
            <ToggleReadButton
                isRead={notification.read}
                onToggle={handleToggleReadStatus}
                supportsHover={supportsHover}
            />
        </ListItem>
    );
}
